import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
  deletePracticePaper,
  getPracticePaper,
  listPracticePapers,
  type PracticePaperSession,
} from "../../../../api/studentPracticePapers"
import {
  ASSESSMENT_COMPLETE,
  QUESTION_GENERATION_COMPLETE,
} from "../../../../api/studentNotifications"
import useLearnerNotifications from "../../../../hooks/useLearnerNotifications"
import type { PaperSession } from "../types"
import { awaitingMarks } from "../utils/paperStatus"

export const PAPER_LIST_PAGE_SIZE = 5

export interface PaperListPagination {
  page: number
  limit: number
  total: number
  total_pages: number
}

const emptyPagination = (page = 1): PaperListPagination => ({
  page,
  limit: PAPER_LIST_PAGE_SIZE,
  total: 0,
  total_pages: 0,
})

const paperIdFromNotificationData = (
  data: Record<string, unknown> | null | undefined
): string | null => {
  const raw = data?.paper_id
  return typeof raw === "string" && raw.trim() ? raw.trim() : null
}

const paperStillWaiting = (
  session: PaperSession,
  notificationType: string | undefined
) => {
  if (notificationType === QUESTION_GENERATION_COMPLETE) {
    return session.status === "preparing"
  }
  if (notificationType === ASSESSMENT_COMPLETE) {
    return awaitingMarks(session)
  }
  return false
}

const usePaperSessions = () => {
  const { notifications } = useLearnerNotifications()
  const [sessions, setSessions] = useState<PaperSession[]>([])
  const [page, setPage] = useState(1)
  const [pagination, setPagination] = useState<PaperListPagination>(emptyPagination)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const pageRef = useRef(page)
  pageRef.current = page
  const handledPaperNotifs = useRef<Set<string>>(new Set())
  // A notification refetch can land while a list request is still in flight.
  // Keep that newer paper so the list response cannot put the card back.
  const livePapers = useRef<Map<string, PaperSession>>(new Map())

  const refresh = useCallback(async (nextPage = pageRef.current) => {
    setLoading(true)
    setError(null)
    try {
      const res = await listPracticePapers(nextPage, PAPER_LIST_PAGE_SIZE)
      if (res.data?.success && Array.isArray(res.data.data)) {
        const listed = res.data.data as PracticePaperSession[]
        setSessions(
          listed.map((session) => {
            const live = livePapers.current.get(session.id)
            if (!live) return session
            const listedWaiting =
              session.status === "preparing" || awaitingMarks(session)
            if (!listedWaiting) {
              livePapers.current.delete(session.id)
              return session
            }
            return live
          })
        )
        const meta = res.data.pagination as
          | {
              page?: number
              limit?: number
              total?: number
              total_pages?: number
            }
          | undefined
        const total = meta?.total ?? res.data.data.length
        const limit = meta?.limit ?? PAPER_LIST_PAGE_SIZE
        setPagination({
          page: meta?.page ?? nextPage,
          limit,
          total,
          total_pages:
            meta?.total_pages ??
            (limit ? Math.ceil(total / limit) : 0),
        })
      } else {
        setSessions([])
        setPagination(emptyPagination(nextPage))
        setError(res.data?.message || "Could not load practice papers")
      }
    } catch (err) {
      setSessions([])
      setPagination(emptyPagination(nextPage))
      setError("Could not load practice papers")
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh(page)
  }, [page, refresh])

  const saveSession = useCallback((session: PaperSession) => {
    const onFirstPage = pageRef.current === 1
    let inserted = false
    setSessions((prev) => {
      const idx = prev.findIndex((s) => s.id === session.id)
      if (idx !== -1) {
        return prev.map((s, i) => (i === idx ? session : s))
      }
      inserted = true
      if (!onFirstPage) return prev
      return [session, ...prev].slice(0, PAPER_LIST_PAGE_SIZE)
    })
    if (!onFirstPage) {
      setPage(1)
      return
    }
    if (inserted) {
      setPagination((prev) => {
        const total = prev.total + 1
        return {
          page: 1,
          limit: PAPER_LIST_PAGE_SIZE,
          total,
          total_pages: Math.max(1, Math.ceil(total / PAPER_LIST_PAGE_SIZE)),
        }
      })
    }
  }, [])

  // Socket notifications already reach the bell. Apply the same events to
  // papers on screen: generation finished, or marks arrived (list + analytics).
  useEffect(() => {
    const fresh = notifications.filter(
      (n) =>
        (n.notification_type === QUESTION_GENERATION_COMPLETE ||
          n.notification_type === ASSESSMENT_COMPLETE) &&
        n.id &&
        !handledPaperNotifs.current.has(n.id)
    )
    if (!fresh.length) return

    for (const n of fresh) {
      const paperId = paperIdFromNotificationData(n.data)
      if (!paperId) {
        if (n.notification_type !== QUESTION_GENERATION_COMPLETE) {
          handledPaperNotifs.current.add(n.id)
          continue
        }
        if (!sessions.some((s) => s.status === "preparing")) continue
        handledPaperNotifs.current.add(n.id)
        void refresh()
        continue
      }

      const listed = sessions.find((s) => s.id === paperId)
      // Paper not loaded yet — retry when the list arrives. Don't drop the event.
      if (!listed) continue
      if (!paperStillWaiting(listed, n.notification_type)) {
        handledPaperNotifs.current.add(n.id)
        continue
      }

      handledPaperNotifs.current.add(n.id)
      void (async () => {
        try {
          const res = await getPracticePaper(paperId)
          if (res.data?.success && res.data.data) {
            const next = res.data.data as PracticePaperSession
            livePapers.current.set(next.id, next)
            saveSession(next)
            return
          }
        } catch {
          // fall through
        }
        await refresh()
      })()
    }
  }, [notifications, sessions, refresh, saveSession])

  const deleteSession = useCallback(async (id: string) => {
    try {
      await deletePracticePaper(id)
    } catch {
      await refresh()
      throw new Error("Could not delete practice paper")
    }
    const currentPage = pageRef.current
    setPagination((prev) => {
      const total = Math.max(0, prev.total - 1)
      const totalPages = total
        ? Math.max(1, Math.ceil(total / PAPER_LIST_PAGE_SIZE))
        : 0
      const nextPage =
        total === 0 ? 1 : Math.min(currentPage, Math.max(1, totalPages))
      if (nextPage !== currentPage) {
        setPage(nextPage)
      } else {
        void refresh(currentPage)
      }
      return {
        page: nextPage,
        limit: PAPER_LIST_PAGE_SIZE,
        total,
        total_pages: totalPages,
      }
    })
  }, [refresh])

  const sortedSessions = useMemo(
    () =>
      [...sessions].sort(
        (a, b) =>
          new Date(b.lastActiveAt).getTime() - new Date(a.lastActiveAt).getTime()
      ),
    [sessions]
  )

  return {
    sessions: sortedSessions,
    pagination,
    page,
    setPage,
    loading,
    error,
    refresh,
    saveSession,
    deleteSession,
  }
}

export default usePaperSessions
